import {
  parseClientStreamStatsMessage,
  isCongestionMessage,
  isDebugWebrtcLoggingEnabled,
  type ClientStreamStatsMessage,
  type CongestionMessage,
} from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';

/**
 * DEBUG BUILD ONLY — log every guest health message the host routes.
 *
 * Marks the ARRIVAL end of the wire: logger prefix `[HostClientStats.route]`, gated LIVE
 * via `isDebugWebrtcLoggingEnabled()` (or an explicit `debug: true` override) so a late
 * `?debug` / `armWebrtcDebugLogging()` after construction still engages the gate. A ctor
 * snapshot would miss that. Carries every field the guest measured plus whether the message
 * was a measured `clientStats` or a legacy `congestion` verdict, so a replay can tell
 * "guest sent nothing" from "guest sent, host received" from "host received but the
 * ladder disagreed".
 *
 * `onClientStats` MAY return a verdict marker ('critical' | 'elevated' | 'none' |
 * 'insufficient-data' | 'unchanged'). When it reports 'unchanged', the arrival is logged
 * as `[HostClientStats.silent]` in addition to the route line: the guest's stats reached
 * the host but produced no recovery transition. That is the Android "nothing adapts"
 * shape, and without this line a replay cannot tell "no inputs" from "inputs, verdict
 * unchanged".
 */
export type ClientStatsRouterDeps = {
  onClientStats: (
    peerId: string,
    msg: ClientStreamStatsMessage,
  ) => 'critical' | 'elevated' | 'none' | 'insufficient-data' | 'unchanged' | void;
  /** Legacy path, kept for clients older than the clientStats contract. */
  onLegacyCongestion: (peerId: string, msg: CongestionMessage) => void;
  /**
   * Force debug on (`true`) or off (`false`). When omitted, the live
   * `isDebugWebrtcLoggingEnabled()` gate is consulted on every route.
   */
  debug?: boolean;
};

/**
 * Single place that decides what a guest datachannel health message means.
 *
 * Exists as its own class because both call sites — PeerCoordinator's datachannel handler
 * and PeerConnectionManager — are already far past the file-size limit, and neither should
 * grow another parsing branch. Callers delegate; this owns the dispatch.
 *
 * Both message shapes are accepted deliberately. Host and guest deploy independently, so a
 * newer host must keep understanding an older guest's `congestion` verdicts, and an older
 * host must be able to ignore a newer guest's `clientStats` without breaking (it falls
 * through to the type registry as an unrecognised message).
 */
export class ClientStatsRouter {
  private readonly lastReport = new Map<string, { epoch: number; seq: number }>();
  constructor(private readonly deps: ClientStatsRouterDeps) {}

  public clearPeer(peerId: string): void { this.lastReport.delete(peerId); }

  /** Live debug gate — honours late `?debug` / arm, and explicit ctor overrides. */
  private isDebugEnabled(): boolean {
    if (this.deps.debug === true) return true;
    if (this.deps.debug === false) return false;
    return isDebugWebrtcLoggingEnabled();
  }

  /** Returns true when the message was recognised and dispatched. */
  public route(peerId: string, input: unknown): boolean {
    const parsed = parseClientStreamStatsMessage(input);
    if (parsed) {
      if (parsed.seq !== undefined) {
        const previous = this.lastReport.get(peerId);
        // Missing path evidence is unavailable, not evidence of a prior path.
        // Route it so recovery can invalidate its healthy dwell, while keeping
        // sequence deduplication and explicit old-epoch rejection intact.
        const epoch = parsed.recoveryEvidence?.path?.epoch ?? previous?.epoch ?? 0;
        if (previous && (epoch < previous.epoch || (epoch === previous.epoch && parsed.seq <= previous.seq))) return true;
        this.lastReport.set(peerId, { epoch, seq: parsed.seq });
      }
      const verdict = this.deps.onClientStats(peerId, parsed);
      if (this.isDebugEnabled()) {
        const routeLine = JSON.stringify({
          t: new Date().toISOString(),
          tag: '[HostClientStats.route]',
          peerId,
          kind: 'clientStats',
          // Hoisted out of `fields` so a replay can order samples by the clock that kept
          // running. `t` is host arrival, which collapses to one instant whenever the host
          // renderer was throttled and released a backlog at once.
          ...(typeof parsed.sampledAtEpochMs === 'number'
            ? { guestSampledAtEpochMs: parsed.sampledAtEpochMs }
            : {}),
          fields: { ...parsed },
        });
        console.log('[HostClientStats.route]', {
          peerId,
          kind: 'clientStats',
          fields: { ...parsed },
        });
        sinkWebrtcDebugLine(routeLine);
        if (verdict === 'unchanged') {
          console.log('[HostClientStats.silent]', {
            peerId,
            reason: 'clientStats arrived, ladder produced no transition',
            fields: { ...parsed },
          });
          sinkWebrtcDebugLine(
            JSON.stringify({
              t: new Date().toISOString(),
              tag: '[HostClientStats.silent]',
              peerId,
              reason: 'clientStats arrived, ladder produced no transition',
              fields: { ...parsed },
            }),
          );
        }
      }
      return true;
    }
    if (isCongestionMessage(input)) {
      if (this.isDebugEnabled()) {
        console.log('[HostClientStats.route]', {
          peerId,
          kind: 'legacy-congestion',
          fields: { ...input },
        });
      }
      this.deps.onLegacyCongestion(peerId, input);
      return true;
    }
    return false;
  }
}
